import { useEffect } from 'react'
import Head from 'next/head'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import { serverSideTranslations } from 'next-i18next/serverSideTranslations'
import { setDashboardTitle } from '@/redux/themeSlice'
import TagManager from 'react-gtm-module'
import { useDispatch } from 'react-redux'
import { socialLogin } from '../features/auth/apis/authSlice'
import { errorHandling, openNotification } from '../utils/utils'
import { getPostAuthRedirectPath } from '../utils/authRedirect'
import { getMemebershipPackageDetails } from '@/utils/serverUtils'

import Layout from '../components/layout-components/homeLayout/Layout'
import Welcome from '@/features/homePage/components/Welcome'
import HomePageMembershipPromo from '@/components/shared-components/HomePageMembershipPromo'
import ComplianceNoticeBar from '@/components/shared-components/ComplianceNoticeBar'

const WhyUs = dynamic(
  () => import('../components/shared-components/WhyUs'),
  {
    ssr: true,
  }
)

const Reviews = dynamic(() =>
  import('../components/shared-components/blocks/Reviews')
)

const AppDownloadBanner = dynamic(() =>
  import('@/features/homePage/components/AppDownloadBanner')
)

const HomeFaq = dynamic(
  () => import('@/features/homePage/components/HomeFaq'),
  { ssr: true }
)

const OurServices = dynamic(() =>
  import('../features/homePage/components/OurServices')
)

const Home = ({ membershipPackageDetails }) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(setDashboardTitle('Home'))
  }, [dispatch])

  const { query, push } = useRouter()

  // SOCIAL LOGIN
  useEffect(() => {
    if (query.code) {
      dispatch(socialLogin(query.code))
        .unwrap()
        .then(() => {
          const nextRedirectPath = getPostAuthRedirectPath(query.redirection_link)

          if (localStorage.getItem('redirection_link')) {
            localStorage.removeItem('redirection_link')
          }

          if (nextRedirectPath) {
            push(nextRedirectPath)
          } else if (localStorage.getItem('SMARTCAF_conversation_token')) {
            push('/dashboard/tickets')
          } else if (localStorage.getItem('SMARTCAF_SERVICE_ID')) {
            push(
              `/dashboard/servizi/${localStorage.getItem(
                'SMARTCAF_SERVICE_ID'
              )}/request`
            )
            localStorage.removeItem('SMARTCAF_SERVICE_ID')
          } else {
            push('/dashboard/servizi')
          }

          if (query.operation === 'register') {
            const tagManagerArgs = {
              dataLayer: {
                event: 'register',
                eventCategory: 'Registration',
                eventAction: null,
                eventLabel: 'user',
              },
              dataLayerName: 'PageDataLayer',
            }
            TagManager.dataLayer(tagManagerArgs)
          } else {
            const tagManagerArgs = {
              dataLayer: {
                event: 'login',
                eventCategory: 'Login',
                eventAction: 'user',
              },
              dataLayerName: 'PageDataLayer',
            }
            TagManager.dataLayer(tagManagerArgs)
          }
        })
        .catch((error) => openNotification('error', errorHandling(error)))
    }
  }, [dispatch, push, query.code, query.operation, query.redirection_link])

  return (
    <div>
      <Head>
        <title>SmartCAF | Il Primo CAF Online in Italia</title>
        <link rel='icon' href='/favicon.ico' />
        <meta
          name='description'
          content='Evita le code, siamo il primo CAF in Italia completamente online! Richiedi ISEE, 730, NASpI e altri servizi come disoccupazione e assegno unico comodamente da casa.'
        />
        <meta name="keywords" content="CAF online, ISEE online, modello 730, patronato online, NASpI, assegno unico, dichiarazione redditi" />
        <link rel="canonical" href="https://www.smartcaf.it/" />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.smartcaf.it/" />
        <meta property="og:title" content="SmartCAF | Il Primo CAF Online in Italia" />
        <meta property="og:description" content="Evita le code, siamo il primo CAF in Italia completamente online! Richiedi ISEE, 730, NASpI e altri servizi come disoccupazione e assegno unico comodamente da casa." />
        <meta property="og:image" content="https://www.smartcaf.it/imgs/logo.png" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.smartcaf.it/" />
        <meta property="twitter:title" content="SmartCAF | Il Primo CAF Online in Italia" />
        <meta property="twitter:description" content="Evita le code, siamo il primo CAF in Italia completamente online! Richiedi ISEE, 730, NASpI e altri servizi come disoccupazione e assegno unico comodamente da casa." />
        <meta property="twitter:image" content="https://www.smartcaf.it/imgs/logo.png" />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Organization",
              "name": "SmartCAF",
              "url": "https://www.smartcaf.it",
              "logo": "https://www.smartcaf.it/imgs/logo.png",
              "contactPoint": {
                "@type": "ContactPoint",
                "telephone": "+39-02-91443332",
                "contactType": "customer service"
              },
              "sameAs": [
                "https://www.facebook.com/Smart-Caf-101772231520624/",
                "https://www.instagram.com/smartcaf/?hl=it",
                "https://www.linkedin.com/company/smart-caf/"
              ]
            })
          }}
        />
      </Head>
      <ComplianceNoticeBar />
      <Welcome />
      <HomePageMembershipPromo membershipPackageDetails={membershipPackageDetails} />
      <OurServices /> 
      <WhyUs />
      <AppDownloadBanner />
      <HomeFaq />
      <Reviews />
    </div>
  )
}

Home.Layout = Layout
export default Home

export async function getStaticProps({ locale }) {
  let membershipPackageDetails = null
  try {
    membershipPackageDetails = await getMemebershipPackageDetails()
  } catch (error) {
    console.error('Failed to fetch membership package details', error)
  }

  return {
    props: {
      ...(await serverSideTranslations(locale, [
        'Home',
        'Services',
        'HomeLayout',
      ])),
      membershipPackageDetails,
    },
    revalidate: 900,
  }
}
